import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { UserProfile } from '../lib/firebase';
import { UserRole } from '../types/user';
import { deduplicateKundalis } from '../utils/kundaliDedupe';
import { formatDOB } from '../utils/dateUtils';
import { PlusCircle, BookOpen, Star, X, ChevronRight, User, Search } from 'lucide-react';
import { SwastikLogo } from './SwastikLogo';
import { LanguageSwitcher } from './LanguageSwitcher';

interface StartPopupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateNew: () => void;
  onLoadSavedKundali: (k: CompleteKundali) => void;
  savedKundalis: CompleteKundali[];
  lang?: Language;
  setLang?: (lang: Language) => void;
  currentUser?: UserProfile | null;
}

export const StartPopupModal: React.FC<StartPopupModalProps> = ({
  isOpen,
  onClose,
  onCreateNew,
  onLoadSavedKundali,
  savedKundalis,
  lang = 'en',
  setLang,
  currentUser
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const uniqueSavedKundalis = deduplicateKundalis(savedKundalis);
  const isHi = lang === 'hi';

  const userScopedKundalis = uniqueSavedKundalis.filter((sk: any) => {
    if (!currentUser) return false;
    if (currentUser.role === UserRole.GUEST || (currentUser.role as string) === 'guest') {
      return sk.userId === currentUser.uid || sk.userId === 'guest';
    }
    return sk.userId === currentUser.uid || (sk.userEmail && sk.userEmail.toLowerCase() === currentUser.email?.toLowerCase());
  });

  const filteredKundalis = userScopedKundalis.filter((sk) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      sk.birthDetails.name.toLowerCase().includes(q) ||
      sk.birthDetails.placeName.toLowerCase().includes(q) ||
      sk.birthDetails.date.includes(q) ||
      (sk.houses[0]?.sign && sk.houses[0].sign.toLowerCase().includes(q))
    );
  });

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-astro-text/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden text-astro-text flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-astro-card border-b border-astro-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <SwastikLogo size="sm" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-astro-primary font-serif">
                {isHi ? 'वेदांग ज्योतिषम्' : 'Vedang Jyotisham'}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'ब्रह्मांडीय विज्ञान एवं वैदिक गणना' : 'Celestial Science & Vedic Calculation'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {setLang && <LanguageSwitcher lang={lang} setLang={setLang} />}
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50 transition-colors"
              title={isHi ? 'बंद करें' : 'Close modal'}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Selection Body */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Main Action Card */}
          <div className="flex justify-center">
            {/* Create New Card */}
            <button
              onClick={() => {
                onClose();
                onCreateNew();
              }}
              className="w-full max-w-sm p-5 bg-astro-bg hover:bg-astro-card border border-astro-border hover:border-astro-primary/40 rounded-2xl text-left transition-all group shadow-xs flex flex-col justify-between space-y-4 cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <div className="w-12 h-12 rounded-xl bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary">
                  <PlusCircle className="w-6 h-6" />
                </div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2 py-0.5 rounded-md">
                  {isHi ? 'नई कुण्डली' : 'New Chart'}
                </span>
              </div>
              <div>
                <h3 className="text-base font-bold text-astro-text group-hover:text-astro-primary transition-colors font-serif">
                  {isHi ? 'नई कुण्डली बनाएं' : 'New Kundali'}
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1 leading-relaxed">
                  {isHi
                    ? 'सटीक लग्न, चंद्र राशि एवं विंशोत्तरी दशा हेतु जन्म विवरण (नाम, तिथि, समय व स्थान) प्रविष्ट करें।'
                    : 'Enter fresh birth details (name, date, time, and city) for exact Lagna, Moon, and Dasha calculations.'}
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-bold text-astro-primary pt-1">
                <span>{isHi ? 'फॉर्म खोलें' : 'Start Blank Form'}</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>

          {/* Existing Kundalis Sub-List */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between border-b border-astro-border pb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'आपकी सहेजी गई कुण्डलियाँ' : 'Your Saved Kundalis'} ({userScopedKundalis.length})
              </h4>
              <span className="text-[10px] text-astro-secondary-text">
                {isHi ? 'कुण्डली देखने हेतु क्लिक करें' : 'Click any row to open chart'}
              </span>
            </div>

            {/* Search Input for Saved Kundalis */}
            {userScopedKundalis.length > 0 && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-astro-secondary-text" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isHi ? 'कुंडलियाँ खोजें...' : 'Search your saved kundalis...'}
                  className="w-full pl-9 pr-7 py-1.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text placeholder:text-astro-muted focus:outline-none focus:border-astro-primary"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-astro-secondary-text hover:text-astro-text"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
              {filteredKundalis.length > 0 ? (
                filteredKundalis.map((sk, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      onLoadSavedKundali(sk);
                      onClose();
                    }}
                    className="w-full p-3 bg-astro-card hover:bg-astro-bg border border-astro-border hover:border-astro-primary/40 rounded-xl text-left transition-all flex items-center justify-between group cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary font-bold text-xs">
                        <User className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-bold text-astro-text text-sm group-hover:text-astro-primary transition-colors">
                          {sk.birthDetails.name}
                        </div>
                        <div className="text-[11px] text-astro-secondary-text">
                          {formatDOB(sk.birthDetails.date)} at {sk.birthDetails.time} • {sk.birthDetails.placeName}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      <span className="font-semibold text-astro-primary">
                        {sk.houses[0]?.sign} {isHi ? 'लग्न' : 'Lagna'}
                      </span>
                    </div>
                  </button>
                ))
              ) : userScopedKundalis.length > 0 ? (
                <div className="p-4 text-center text-xs text-astro-secondary-text bg-astro-card rounded-xl border border-astro-border">
                  {isHi ? 'खोज अनुसार कोई कुंडली नहीं मिली।' : 'No matching kundalis found.'}
                </div>
              ) : (
                <div className="p-6 text-center text-xs text-astro-secondary-text bg-astro-card rounded-xl border border-astro-border">
                  {isHi
                    ? 'कोई सहेजी गई कुण्डली नहीं है। नई कुण्डली बनाने हेतु ऊपर क्लिक करें।'
                    : 'No custom saved Kundalis yet. Click New Kundali above to calculate and save fresh birth charts.'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-astro-card border-t border-astro-border flex items-center justify-between text-xs text-astro-secondary-text">
          <span>© Vedanga Jyotisha Engine</span>
          <span className="text-astro-primary font-medium">Lahiri Ayanamsha • High Precision Ephemeris</span>
        </div>
      </div>
    </div>
  );
};

